import { nanoid } from 'nanoid';
import { Attendee } from '@/types/attendee';
import { AdminState, AdminAction } from '@/types/adminTypes';

/**
 * Core service for interacting with localStorage
 * Provides fallback methods when Supabase is unavailable
 */
export const getLocalStorageItem = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error getting ${key} from localStorage:`, error);
    return defaultValue;
  }
};

export const setLocalStorageItem = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error setting ${key} in localStorage:`, error);
  }
};

// Type-specific helper functions
export const getAdminActions = (): AdminAction[] => {
  return getLocalStorageItem<AdminAction[]>('adminActions', []);
};

export const setAdminActions = (actions: AdminAction[]): void => {
  setLocalStorageItem('adminActions', actions);
};

export const getAdminState = (): AdminState => {
  const defaultState: AdminState = {
    lastActivity: new Date().toISOString(),
    activeAdmins: [],
    lastSync: new Date().toISOString()
  };
  
  return getLocalStorageItem<AdminState>('adminState', defaultState);
};

export const setAdminState = (state: AdminState): void => {
  setLocalStorageItem('adminState', state);
};

export const getAttendees = (): Attendee[] => {
  return getLocalStorageItem<Attendee[]>('attendees', []);
};

export const setAttendees = (attendees: Attendee[]): void => {
  setLocalStorageItem('attendees', attendees);
};
